import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiOutlineChevronDown } from "react-icons/hi2";
import { useAnchoredMenu } from "hooks/useAnchoredMenu";
import TimesheetSearchField from "../../components/TimesheetRevamp/TimesheetSearchField";
import "components/common/DropDown/ThemedDropdown.css";

/**
 * Single-select filter pill with the timesheet search menu.
 * The chosen value stays one id or "all", same as the plain dropdown.
 * The menu stays inside the dialog so focus can sit in the search field.
 */
const BulkAnalyseSearchSelect = ({
  value,
  options = [],
  onChange,
  placeholder = "Select",
  searchPlaceholder = "Search...",
  emptyLabel = "No matches",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuStyle = useAnchoredMenu({ open, anchorRef: buttonRef });

  const selected = options.find((option) => option.value === value);
  const displayLabel = selected?.label || placeholder;

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) =>
      String(option.label || "")
        .toLowerCase()
        .includes(query)
    );
  }, [options, search]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      return undefined;
    }
    const onPointer = (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      if (search) {
        setSearch("");
        return;
      }
      setOpen(false);
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, search]);

  const choose = (option) => {
    onChange?.(option.value, option);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative min-w-0 w-full">
      <button
        ref={buttonRef}
        type="button"
        className={`themed-dropdown-trigger flex w-full items-center gap-2 px-[11px] text-left text-[13px] font-semibold leading-none text-[#0F172A] ${
          open ? "is-open" : ""
        }`}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="min-w-0 flex-1 truncate">{displayLabel}</span>
        <HiOutlineChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-[#94A3B8] transition-transform duration-150 ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>
      {open && menuStyle
        ? createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="pointer-events-auto flex flex-col overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
          role="listbox"
        >
          <div className="shrink-0 border-b border-[#E5E7EB] p-2 sm:p-3">
            <TimesheetSearchField
              value={search}
              onChange={setSearch}
              placeholder={searchPlaceholder}
              autoFocus
            />
          </div>
          <ul
            className="themed-dropdown-scroll min-h-0 flex-1 py-1"
            style={{ maxHeight: Math.max(36, (menuStyle.maxHeight || 240) - 52) }}
            onWheel={(event) => event.stopPropagation()}
            onTouchMove={(event) => event.stopPropagation()}
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2.5 text-[13px] text-[#94A3B8]">{emptyLabel}</li>
            ) : (
              filtered.map((option) => {
                const isSelected = option.value === value;
                return (
                  <li key={String(option.value)}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`flex w-full items-center px-3 py-2 text-left text-[13px] ${
                        isSelected
                          ? "bg-[#EEF2FB] font-semibold text-[#4A6BB6]"
                          : "font-medium text-[#0F172A] hover:bg-[#F8FAFC]"
                      }`}
                      onClick={() => choose(option)}
                    >
                      <span className="min-w-0 flex-1 truncate">{option.label}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>,
        document.querySelector("[data-bulk-analyse-menu-root]") || document.body
      ) : null}
    </div>
  );
};

export default BulkAnalyseSearchSelect;
